import { useStore } from '@nanostores/react';
import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/cloudflare';
import { redirect } from '@remix-run/cloudflare';
import {
  json,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useMatches,
  useRouteLoaderData,
} from '@remix-run/react';
import type { User } from '@sentry/browser';
import { withSentry } from '@sentry/remix';
import tailwindReset from '@unocss/reset/tailwind-compat.css?url';
import xtermStyles from '@xterm/xterm/css/xterm.css?url';
import React, { useEffect } from 'react';
import { cssTransition, ToastContainer } from 'react-toastify';
import reactToastifyStyles from 'react-toastify/dist/ReactToastify.css?url';
import { createHead } from 'remix-island';
import { ClientOnly } from 'remix-utils/client-only';
import { handleWithAuth } from './lib/.server/auth-middleware';
import { withUserData } from './lib/.server/user';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { sendAnalyticsEvent } from './lib/analytics.client';
import { DEFAULT_THEME, kTheme, themeStore } from './lib/stores/theme';
import globalStyles from './styles/index.scss?url';
import { stripIndents } from './utils/stripIndent';
import { Banner } from '~/components/banner/Banner';
import { buildUserSession } from '~/lib/.server/authentication-flow';
import { fetchCachedBanner } from '~/lib/.server/banner';
import { logEvent } from '~/lib/.server/logging';
import { CLIENT_ID, CLIENT_ORIGIN } from '~/lib/constants';
import { sendSentryError } from '~/lib/sentry.client';
import { serverFetch } from '~/lib/server-fetch';
import { AnalyticsAction } from '~/types/analytics';
import type { RouteMatch } from '~/types/routes';
import type { Theme } from '~/types/theme';

import 'virtual:uno.css';

const SENTRY_ID = '63c9c85c78964d27b2264b54d8439bd3';
const SENTRY_USER_ELEMENT = 'sentry-initial-user-data';

const toastAnimation = cssTransition({
  enter: 'animated fadeInRight',
  exit: 'animated fadeOutRight',
});

export const links: LinksFunction = () => [
  {
    rel: 'icon',
    href: '/static/favicon.svg',
    type: 'image/svg+xml',
  },
  { rel: 'stylesheet', href: reactToastifyStyles },
  { rel: 'stylesheet', href: tailwindReset },
  { rel: 'stylesheet', href: globalStyles },
  { rel: 'stylesheet', href: xtermStyles },
  {
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com',
  },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  },
];

function themeCode(storageKey: string, defaultTheme: Theme) {
  if (window.location.pathname !== '/figma') {
    setBoltTheme();
  }

  function setBoltTheme() {
    const htmlElement = document.querySelector('html') as HTMLHtmlElement;

    const localTheme = localStorage.getItem(storageKey);

    const metaElement = document.querySelector('meta[name=bolt-theme]') as HTMLMetaElement | null;

    let theme = metaElement?.content ?? localTheme ?? defaultTheme;

    htmlElement.setAttribute('data-theme-option', theme);

    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      theme = prefersDark ? 'dark' : 'light';
    }

    htmlElement.setAttribute('data-theme', theme);
  }
}

const googleTagSnippet = stripIndents`
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','GTM-58KJM59B');
`;

function createWorkaroundGlobal() {
  /**
   * A bug in wrangler injects a __name call in these script tags.
   *
   * @see https://github.com/cloudflare/workers-sdk/issues/7107#issuecomment-2454829854
   */
  // eslint-disable-next-line
  // @ts-ignore
  window['__' + 'name'] = () => {
    // noop
  };
}

function sentryLoader(environment: string, userElementId: string) {
  window.sentryOnLoad = () => {
    // https://docs.sentry.io/platforms/javascript/configuration/
    Sentry.init({
      release: import.meta.env.SENTRY_RELEASE,

      // ignore any transaction in a user project path /~/*
      ignoreTransactions: ['/~/.*'],
      ignoreErrors: [],

      // https://docs.sentry.io/platforms/javascript/session-replay/
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sampleRate: 0.02,
      tracesSampleRate: 0.05,
      enabled: environment !== 'development',
      debug: false,
      allowUrls: ['bolt.new', 'pages.dev'],
      environment,
    });

    const userSource = document.getElementById(userElementId)?.textContent;

    if (!userSource) {
      return;
    }

    Sentry.setUser(JSON.parse(userSource ?? 'null'));
  };
}

// prevent modifications to the DOM until hydration is finished
function blockDOMMutations() {
  const observer = new MutationObserver((mutations) => {
    observer.disconnect();

    // we iterate backwards so that the oldValue is always correct by the end, in the case of simultaneous changes
    mutations.reverse().forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          /**
           * Modifications to the head do not affect hydration (vite uses this for HMR styles).
           *
           * Nodes added as children of the body do not affect hydration (WebContainer uses this for the iframe).
           */
          if ([document.head, document.body, document.documentElement].includes(node.parentNode as HTMLElement)) {
            return;
          }

          if (node.parentNode) {
            node.parentNode.removeChild(node);
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (mutation.previousSibling) {
            mutation.target.insertBefore(node, mutation.previousSibling.nextSibling);
          } else if (mutation.target) {
            mutation.target.appendChild(node);
          }
        });
      }

      if (mutation.type === 'attributes') {
        const { target, attributeName, oldValue } = mutation;

        if (attributeName) {
          if (oldValue === null) {
            (target as HTMLElement).removeAttribute(attributeName);
          } else {
            (target as HTMLElement).setAttribute(attributeName, oldValue);
          }
        }
      }

      if (mutation.type === 'characterData') {
        (mutation.target as CharacterData).data = mutation.oldValue ?? '';
      }
    });

    observe();
  });

  function observe() {
    observer.observe(document, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeOldValue: true,
      characterData: true,
      characterDataOldValue: true,
    });
  }

  window.addEventListener('DOMContentLoaded', observe);

  window.__allowDOMMutations = () => {
    observer.disconnect();
    window.__loadingPrompt = document.querySelector('textarea')?.value;
  };
}

export async function loader(args: LoaderFunctionArgs) {
  const bannerPromise = fetchCachedBanner(args.context);
  const redirectInfo = await handeIdProviderInitiatedSSOSession(args);

  if (redirectInfo) {
    return redirect(redirectInfo.url, { headers: redirectInfo.headers });
  }

  return handleWithAuth(
    args,
    async (_args, session) => {
      const { user } = await withUserData({}, session, args.request);

      return json({ banner: await bannerPromise, user });
    },
    async () => json({ banner: await bannerPromise, user: null }),
  );
}

type RedirectInfo = {
  url: string;
  headers: Record<string, string>;
};

/**
 * Is this an Identity Provider initiated SSO session? If so,
 * StackBlitz provides us with a One-Time-Code (OTC) redeemable
 * for an OAuth token for a user, which we should use to create a session.
 *
 * Initiate the session, and yield the redirect info we should use.
 */
async function handeIdProviderInitiatedSSOSession(args: LoaderFunctionArgs): Promise<RedirectInfo | null> {
  const url = new URL(args.request.url);
  const otc = url.searchParams.get('otc');

  if (otc) {
    try {
      const tokens = await exchangeOtcForOauthToken(otc, args.request.url);

      if (tokens) {
        const { init } = await buildUserSession(args, tokens);

        const redirectUrl = new URL(args.request.url);
        redirectUrl.searchParams.delete('otc');

        return { url: redirectUrl.toString(), headers: init.headers };
      }
    } catch (error) {
      sendSentryError(new Error('[SSO IDP-initated-session] Token exchange failed:', { cause: error }));
    }
  }

  return null;
}

export const Head = createHead(() => {
  const rootLoaderData = useRouteLoaderData<typeof loader>('root');
  const matches = useMatches() as RouteMatch[];

  const user: User | undefined = rootLoaderData?.user
    ? {
        id: rootLoaderData.user.id,
        username: rootLoaderData.user.username,
        email: rootLoaderData.user.email,
      }
    : undefined;

  // if any of the matches have a handle.noTracking property set to true, we will not include tracking scripts
  const includeTrackingScripts = matches.every((match) => match.handle?.tracking !== false);

  return (
    <>
      {import.meta.env.VITE_REACT_SCAN && (
        <script crossOrigin="anonymous" src="https://cdn.jsdelivr.net/npm/react-scan/dist/auto.global.js" />
      )}
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <Meta />
      <Links />
      <script dangerouslySetInnerHTML={{ __html: `(${createWorkaroundGlobal})();` }} />
      <script dangerouslySetInnerHTML={{ __html: `(${blockDOMMutations})();` }} />
      <script
        dangerouslySetInnerHTML={{
          __html: `(${themeCode})(${JSON.stringify(kTheme)}, ${JSON.stringify(DEFAULT_THEME)});`,
        }}
      />
      {includeTrackingScripts && import.meta.env.PROD && (
        <script dangerouslySetInnerHTML={{ __html: googleTagSnippet }} />
      )}
      {includeTrackingScripts && user && (
        <script
          id={SENTRY_USER_ELEMENT}
          type="application/json"
          dangerouslySetInnerHTML={{ __html: escapeJSONForScript(JSON.stringify(user)) }}
        ></script>
      )}
      {includeTrackingScripts && (
        <script
          dangerouslySetInnerHTML={{
            __html: `(${sentryLoader})("${import.meta.env.MODE}", "${SENTRY_USER_ELEMENT}");`,
          }}
        />
      )}
      {includeTrackingScripts && !import.meta.env.DEV && (
        <script src={`https://js.sentry-cdn.com/${SENTRY_ID}.min.js`} crossOrigin="anonymous" />
      )}
    </>
  );
});

export function Layout({ children }: { children: React.ReactNode }) {
  const matches = useMatches() as RouteMatch[];
  const rootLoaderData = useRouteLoaderData<typeof loader>('root');

  const theme = useStore(themeStore);

  const { pathname } = useLocation();

  // if any of the matches have a handle.hydrate property set to false, we will not include the Scripts component
  const includeScripts = matches.every((match) => match.handle?.hydrate !== false);

  useEffect(() => {
    if (window.location.pathname !== '/figma') {
      document.querySelector('html')?.setAttribute('data-theme', theme);
    }
  }, [theme]);

  // log page events when the window location changes
  useEffect(() => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Page,
      payload: {},
    });
  }, [pathname]);

  return (
    <>
      <ClientOnly fallback={children}>
        {() => (
          <>
            {rootLoaderData?.banner && <Banner {...rootLoaderData.banner} />}

            <ToastContainer
              closeButton={({ closeToast }) => {
                return (
                  <button className="Toastify__close-button" onClick={closeToast}>
                    <div className="i-ph:x text-lg" />
                  </button>
                );
              }}
              icon={({ type }) => {
                /**
                 * @todo Handle more types if we need them. This may require extra color palettes.
                 */
                switch (type) {
                  case 'success': {
                    return <div className="i-ph:check-bold text-bolt-elements-icon-success text-2xl" />;
                  }
                  case 'error': {
                    return <div className="i-ph:warning-circle-bold text-bolt-elements-icon-error text-2xl" />;
                  }
                  case 'info': {
                    return <div className="i-ph:info-bold text-bolt-elements-icon-info text-2xl" />;
                  }
                  case 'warning': {
                    return <div className="i-ph:warning-bold text-bolt-elements-icon-warning text-2xl" />;
                  }
                }

                return undefined;
              }}
              position="bottom-right"
              pauseOnFocusLoss
              transition={toastAnimation}
            />
            {children}
          </>
        )}
      </ClientOnly>
      <ScrollRestoration />
      {includeScripts && <Scripts />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary showHeading>
      <Outlet />
    </ErrorBoundary>
  );
}

export default withSentry(App);

if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_RENDER) {
  const { default: wdyr } = await import('@welldone-software/why-did-you-render');

  wdyr(React, {
    include: [/.*/],
    exclude: [/^BrowserRouter/, /^Link/, /^Route/],
    trackHooks: true,
    trackAllPureComponents: true,
  });
}

function escapeJSONForScript(source: string) {
  return source.replace(/<\//g, '<\\/').replace(/<!/g, '<\\!');
}

async function exchangeOtcForOauthToken(
  otc: string,
  requestUrl: string,
): Promise<{ access: string; refresh: string } | null> {
  try {
    const url = new URL(requestUrl);
    const redirectUri = new URL('/oauth2', url.origin).toString();

    const response = await serverFetch(`${CLIENT_ORIGIN}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        redirect_uri: redirectUri,
        otc,
      }),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: status=${response.status}`);
    }

    const data = await response.json<{ access_token: string; refresh_token: string }>();

    return {
      access: data.access_token,
      refresh: data.refresh_token,
    };
  } catch (error: any) {
    Sentry.logger.error('oauth-token-exchange-error', {
      message: error?.message,
      stack: error?.stack,
    });
    logEvent('oauth-token-exchange-error', {
      message: error?.message,
      stack: error?.stack,
    });

    return null;
  }
}
